import {
  ClockIcon,
  PuzzlePieceIcon,
  TrophyIcon,
  UserIcon
} from '@heroicons/react/24/outline';
import { AcademicCapIcon, SunIcon } from '@heroicons/react/20/solid';
import PageLayout from '../common/page-layout/PageLayout';
import { PageSEO } from '../common/blog/elements/SEO';
import Image from 'next/image';
import { useCustomer } from '@/app/context/CustomerContext/useCustomer';
import { useAnalytics } from '@/app/context/AnalyticsContext/useAnalytics';
import { useEffect, useState } from 'react';
import peopleImage from 'public/people.jpg';

export default function About() {
  const { isProEligible, isPro } = useCustomer();
  const { trackEvent, analyticsInstance } = useAnalytics();
  const [didTrackUpgrade, setDidTrackUpgrade] = useState(false);

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    if (!isPro && isProEligible && !didTrackUpgrade) {
      setDidTrackUpgrade(true);
      trackEvent(analyticsInstance, 'upgrade_visible', {
        upgrade_source: 'about'
      });
    }
  }, [isPro, isProEligible]);

  return (
    <PageLayout>
      <PageSEO
        title={'About - Limbiks'}
        description={
          'Learn about how limbiks started and its vision for the future '
        }
      />
      <div className="bg-base-100">
        {/* Header */}

        <main className="relative isolate">
          {/* Background */}
          <div
            aria-hidden="true"
            className="absolute inset-x-0 top-4 -z-10 flex transform-gpu justify-center overflow-hidden blur-3xl"
          >
            <div
              style={{
                clipPath:
                  'polygon(73.6% 51.7%, 91.7% 11.8%, 100% 46.4%, 97.4% 82.2%, 92.5% 84.9%, 75.7% 64%, 55.3% 47.5%, 46.5% 49.4%, 45% 62.9%, 50.3% 87.2%, 21.3% 64.1%, 0.1% 100%, 5.4% 51.1%, 21.4% 63.9%, 58.9% 0.2%, 73.6% 51.7%)'
              }}
              className="aspect-[1108/632] w-[69.25rem] flex-none bg-gradient-to-r from-[#1f2937] to-[tomato] opacity-25"
            />
          </div>

          {/* Header section */}
          <div className="px-6 pt-14 lg:px-8">
            <div className="mx-auto max-w-2xl pt-24 text-center sm:pt-40">
              <h2 className="text-4xl font-bold tracking-tight text-white sm:text-6xl">
                The Limbiks origin story
              </h2>
              <p className="mt-6 text-lg leading-8 text-gray-300">
                More than another AI company.
              </p>
            </div>
          </div>

          {/* Content section */}
          <div className="mx-auto mt-20 max-w-7xl px-6 lg:px-8">
            <div className="mx-auto max-w-2xl lg:mx-0 lg:max-w-none">
              <div className="grid max-w-xl grid-cols-1 gap-8 text-base leading-7 text-gray-300 lg:max-w-none lg:grid-cols-2">
                <div>
                  <p>
                    Limbiks began during the covid quarantine of Spring 2020. As
                    my wife continued to endure her first year of medical
                    school, I watched each day the painful endeavor of flashcard
                    creation. As a Software Engineer, I knew there had to be a
                    way I could help. After studying her actions, and learning
                    the latest advancements in language processing and machine
                    learning, I started building Limbiks. I created software to
                    automatically read her medical school lectures and notes and
                    create flashcards. Soon, the flashcards Limbiks was creating
                    were starting to be shared among her medical school
                    classmates.
                  </p>
                </div>
                <div>
                  <p>
                    I had a feeling there could be more here and the potential
                    time savings for students was limitless. Today, that
                    original processing software remains the backbone of the
                    Limbiks platform. Limbiks has evolved a lot since the early
                    days and now with the emergence of AI I am no longer the
                    only one trying to solve this problem. With that said, I
                    firmly believe Limbiks can one day become a critical piece
                    to the study routine of every student. Give Limbiks a try
                    today and let us know what you think!
                  </p>
                  <p className="mt-8">
                    The best way to support future Limbiks development is to
                    <br />{' '}
                    <a href={'/app/account?interval=month&source=about'}>
                      <span
                        onClick={() => {
                          trackEvent(analyticsInstance, 'upgrade_click', {
                            upgrade_source: 'about'
                          });
                          trackEvent(
                            analyticsInstance,
                            'about_click_pro_subscribe'
                          );
                        }}
                        className="link"
                        style={{ color: 'tomato' }}
                      >
                        subscribe to Limbiks Pro
                      </span>
                    </a>{' '}
                  </p>
                </div>
              </div>
              <dl className="mt-16 grid grid-cols-1 gap-x-8 gap-y-12 sm:mt-20 sm:grid-cols-2 sm:gap-y-16 lg:mt-28 lg:grid-cols-4">
                {stats.map((stat, statIdx) => (
                  <div
                    key={statIdx}
                    className="flex flex-col-reverse gap-y-3 border-l border-white/20 pl-6"
                  >
                    <dt className="text-base leading-7 text-gray-300">
                      {stat.label}
                    </dt>
                    <dd className="text-3xl font-semibold tracking-tight text-white">
                      {stat.value}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>

          {/* Image section */}
          <div className="mt-32 sm:mt-40 xl:mx-auto xl:max-w-7xl xl:px-8">
            <Image
              alt=""
              src={peopleImage}
              className="aspect-[9/4] w-full object-cover xl:rounded-3xl"
            />
          </div>
          <div className="mx-auto mt-32 max-w-7xl px-6 sm:mt-40 lg:px-8">
            <div className="mx-auto max-w-2xl lg:mx-0">
              <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">
                Our Mission
              </h2>
              <p className="mt-6 text-lg leading-8 text-gray-300">
                Time is precious. Deciding what to study and deciding how to
                study should not be something that takes up your time. Instead,
                100% of your time should be spent on optimal learning so you can
                succeed in your classes, and ulimately succeed in life well
                beyond your schooling. Solving this problem is how we approach
                every feature in Limbiks.
              </p>
            </div>
            <dl className="mx-auto mt-16 grid max-w-2xl grid-cols-1 gap-8 text-base leading-7 text-gray-300 sm:grid-cols-2 lg:mx-0 lg:max-w-none lg:gap-x-16">
              {values.map((value) => (
                <div key={value.name} className="relative pl-9">
                  <dt className="inline font-semibold text-white">
                    <value.icon
                      aria-hidden="true"
                      className="absolute left-1 top-1 h-5 w-5 text-primary"
                    />
                    {value.name}
                  </dt>{' '}
                  <dd className="inline">{value.description}</dd>
                </div>
              ))}
            </dl>
          </div>
        </main>
      </div>
    </PageLayout>
  );
}

const stats = [
  { label: 'First Prototype for my wife', value: '2020' },
  { label: 'Launched Website', value: '2021' },
  { label: 'Introduced Limbiks Pro', value: '2023' },
  { label: 'Students helped', value: '100k+' }
];
const values = [
  {
    name: 'Time is precious.',
    description:
      'Nothing is more important than time with friends and family. Time spent studying should never be wasted.',
    icon: ClockIcon
  },
  {
    name: 'Education is key.',
    description:
      'The more we can educate ourselves and master our material, the more we can educate those around us.',
    icon: AcademicCapIcon
  },
  {
    name: 'Be curious.',
    description:
      "Learning and studying is not a new concept, but never stop believing it can't be improved.",
    icon: PuzzlePieceIcon
  },
  {
    name: 'Your results matter.',
    description:
      'If you are not seeing improvements in your study routine and classes, we are doing something wrong.',
    icon: TrophyIcon
  },
  {
    name: 'Be helpful.',
    description:
      'There is no better feeling than when someone does something for you that is genuinely helpful. That is what drives us each day to keep building.',
    icon: UserIcon
  },
  {
    name: 'Go fast.',
    description:
      'The world is changing rapidly, time is of the essence, and it is crucial to strive for efficiency and velocity in everything we do.',
    icon: SunIcon
  }
];
const team = [
  {
    name: 'Leslie Alexander',
    role: 'Co-Founder / CEO',
    imageUrl:
      'https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=8&w=1024&h=1024&q=80',
    location: 'Toronto, Canada'
  }
  // More people...
];
const benefits = [
  'Flashcards',
  'Quiz Questions',
  'Practice Tests',
  'Study Guides',
  'Spaced Repetition',
  'and more...'
];
const footerNavigation = {
  solutions: [
    { name: 'Marketing', href: '#' },
    { name: 'Analytics', href: '#' },
    { name: 'Commerce', href: '#' },
    { name: 'Insights', href: '#' }
  ],
  support: [
    { name: 'Pricing', href: '#' },
    { name: 'Documentation', href: '#' },
    { name: 'Guides', href: '#' },
    { name: 'API Status', href: '#' }
  ],
  company: [
    { name: 'About', href: '#' },
    { name: 'Blog', href: '#' },
    { name: 'Jobs', href: '#' },
    { name: 'Press', href: '#' },
    { name: 'Partners', href: '#' }
  ],
  legal: [
    { name: 'Claim', href: '#' },
    { name: 'Privacy', href: '#' },
    { name: 'Terms', href: '#' }
  ],
  social: [
    {
      name: 'Facebook',
      href: '#',
      icon: (props) => (
        <svg fill="currentColor" viewBox="0 0 24 24" {...props}>
          <path
            fillRule="evenodd"
            d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"
            clipRule="evenodd"
          />
        </svg>
      )
    },
    {
      name: 'Instagram',
      href: '#',
      icon: (props) => (
        <svg fill="currentColor" viewBox="0 0 24 24" {...props}>
          <path
            fillRule="evenodd"
            d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z"
            clipRule="evenodd"
          />
        </svg>
      )
    },
    {
      name: 'X',
      href: '#',
      icon: (props) => (
        <svg fill="currentColor" viewBox="0 0 24 24" {...props}>
          <path d="M13.6823 10.6218L20.2391 3H18.6854L12.9921 9.61788L8.44486 3H3.2002L10.0765 13.0074L3.2002 21H4.75404L10.7663 14.0113L15.5685 21H20.8131L13.6819 10.6218H13.6823ZM11.5541 13.0956L10.8574 12.0991L5.31391 4.16971H7.70053L12.1742 10.5689L12.8709 11.5655L18.6861 19.8835H16.2995L11.5541 13.096V13.0956Z" />
        </svg>
      )
    },
    {
      name: 'GitHub',
      href: '#',
      icon: (props) => (
        <svg fill="currentColor" viewBox="0 0 24 24" {...props}>
          <path
            fillRule="evenodd"
            d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z"
            clipRule="evenodd"
          />
        </svg>
      )
    },
    {
      name: 'YouTube',
      href: '#',
      icon: (props) => (
        <svg fill="currentColor" viewBox="0 0 24 24" {...props}>
          <path
            fillRule="evenodd"
            d="M19.812 5.418c.861.23 1.538.907 1.768 1.768C21.998 8.746 22 12 22 12s0 3.255-.418 4.814a2.504 2.504 0 0 1-1.768 1.768c-1.56.419-7.814.419-7.814.419s-6.255 0-7.814-.419a2.505 2.505 0 0 1-1.768-1.768C2 15.255 2 12 2 12s0-3.255.417-4.814a2.507 2.507 0 0 1 1.768-1.768C5.744 5 11.998 5 11.998 5s6.255 0 7.814.418ZM15.194 12 10 15V9l5.194 3Z"
            clipRule="evenodd"
          />
        </svg>
      )
    }
  ]
};
